import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import React, { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { AuthSessionContext } from '@context/AuthContext'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'

import { PageQuery } from '@utils/ApiClient'
import { formatDate } from '@utils/date.utils'
import { getCentralRowIdConditionally } from '@utils/getItemId'

import { ClientIbContestCampaignDto } from '@model/ClientIbContestCampaignDto'

import { IBContestBadgeMap, StatusBadge } from '@shared/components/StatusBadge/StatusBadge'

interface Props {
  tableQuery: PaginatedQueryResult<ClientIbContestCampaignDto>
  pageQuery?: PageQuery
  setPageQuery(value: PageQuery): void

  setSelectedCampaign(campaignResult: ClientIbContestCampaignDto): void
}

export const IBContestTable: FC<Props> = (props) => {
  const { tableQuery, setSelectedCampaign, pageQuery, setPageQuery } = props
  const { data, isLoading } = tableQuery

  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name

  return (
    <Table isLoading={isLoading} noResults={!data.length}>
      <TableHeader>
        <TableRow>
          <TableHeadSort
            id='Name'
            sort={pageQuery?.sort || undefined}
            sortOrder={pageQuery?.sortOrder}
            setSort={(sort, sortOrder) => {
              setPageQuery?.({
                ...pageQuery,
                sort,
                sortOrder,
              })
            }}
          >
            {t('Campaigns.Campaign name')}
          </TableHeadSort>
          <TableHeadSort
            id='From'
            sort={pageQuery?.sort || undefined}
            sortOrder={pageQuery?.sortOrder}
            setSort={(sort, sortOrder) => {
              setPageQuery?.({
                ...pageQuery,
                sort,
                sortOrder,
              })
            }}
          >
            {t('Campaigns.Start Date')}
          </TableHeadSort>
          <TableHeadSort
            id='To'
            sort={pageQuery?.sort || undefined}
            sortOrder={pageQuery?.sortOrder}
            setSort={(sort, sortOrder) => {
              setPageQuery?.({
                ...pageQuery,
                sort,
                sortOrder,
              })
            }}
          >
            {t('Campaigns.End Date')}
          </TableHeadSort>
          <TableHeadSort
            id='Status.Id'
            sort={pageQuery?.sort || undefined}
            sortOrder={pageQuery?.sortOrder}
            setSort={(sort, sortOrder) => {
              setPageQuery?.({
                ...pageQuery,
                sort,
                sortOrder,
              })
            }}
          >
            {t('Campaigns.Status')}
          </TableHeadSort>
          <TableHead>{t('Campaigns.Results')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((x, index) => (
          <TableRow key={index} {...getCentralRowIdConditionally(index, data.length, 'ib-contest')}>
            <TableCell>{x.name}</TableCell>
            <TableCell>{formatDate(x.from, { formatType: dateFormat, withTime: true })}</TableCell>
            <TableCell>{formatDate(x.to, { formatType: dateFormat, withTime: true })}</TableCell>
            <TableCell>
              <StatusBadge id={x.status.id} badgeMap={IBContestBadgeMap} />
            </TableCell>
            <TableCell>
              <span
                className='is-link'
                onClick={() => {
                  const params = new URLSearchParams(location.search)
                  params.set('contestCampaignId', x.id.toString())
                  navigate(`${location.pathname}?${params.toString()}`, { replace: true })

                  setSelectedCampaign(x)
                }}
              >
                {t('View')}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
